import { iTHREE } from '@treedis/tour-loader';
import sortBy from 'lodash/sortBy';
import { Position, Sweep, SweepPath } from '../../_models/Sweep';

class PathNode {
  public sweep: Sweep;

  public pathLengthFromStart: number;

  public cameFrom: PathNode;

  public estimatePathLength: number;

  constructor(sweep, pathLengthFromStart, cameFrom, estimatePathLength) {
    this.sweep = sweep;
    this.pathLengthFromStart = pathLengthFromStart;
    this.cameFrom = cameFrom;
    this.estimatePathLength = estimatePathLength;
  }

  public estimateFullPathLength() {
    return this.pathLengthFromStart + this.estimatePathLength;
  }
}

const getPathLength = async (from: Position, to: Position) => {
  const THREE = await iTHREE();

  const vector1 = new THREE.Vector3(from.x, from.y, from.z);
  const vector2 = new THREE.Vector3(to.x, to.y, to.z);
  return vector1.distanceTo(vector2);
};

const getNeighbours = (neighbours: number[], currentNode: PathNode, endSweep: Sweep, sweeps: Sweep[]) =>
  neighbours.reduce((acc, neighborIndex) => {
    const sweep = sweeps.find((s) => s.index === neighborIndex);
    if (sweep) {
      acc.push(new PathNode(sweep, getPathLength(currentNode.sweep.position, sweep.position), currentNode, getPathLength(sweep.position, endSweep.position)));
    }
    return acc;
  }, []);

export const getSweepsPath = (allSweeps: Sweep[], start: string, end: string): SweepPath[] => {
  const sweeps = allSweeps.filter((s) => s.tags.indexOf('showcase') || s.uuid === start || s.uuid === end);
  const startSweep = sweeps.find((s) => s.uuid === start);
  const endSweep = sweeps.find((s) => s.uuid === end);
  const closedSet = [];
  let openSet = [];

  const startNode = new PathNode(startSweep, 0, null, getPathLength(startSweep.position, endSweep.position));

  openSet.push(startNode);
  while (openSet.length > 0) {
    const currentNode: any = sortBy(openSet, [(o) => o.estimateFullPathLength()])[0];

    if (currentNode.sweep.uuid === end) {
      closedSet.push(currentNode);
      const path = [];
      for (let i = 0; i < closedSet.length; i += 1) {
        const node = closedSet[i];
        const nextNode = closedSet[i + 1];
        path.push({
          id: node.sweep.uuid,
          position: node.sweep.position,
          rotation: node.sweep.rotation,
          next: nextNode
            ? {
                id: nextNode.sweep.uuid,
                position: nextNode.sweep.position,
                rotation: nextNode.sweep.rotation,
              }
            : null,
        });
      }

      return path;
    }

    openSet = openSet.filter((set) => set.sweep.uuid !== currentNode.sweep.uuid);
    closedSet.push(currentNode);

    const neighbors = getNeighbours(currentNode.sweep.neighbors, currentNode, endSweep, sweeps);
    // eslint-disable-next-line no-loop-func
    neighbors.forEach((neighbor) => {
      if (closedSet.some((set) => set.sweep.index === neighbor.sweep.index)) {
        return;
      }
      const openNode = openSet.find((set) => set.sweep.index === neighbor.sweep.index);
      if (!openNode) {
        openSet.push(neighbor);
      } else if (openNode.pathLengthFromStart > neighbor.pathLengthFromStart) {
        openNode.cameFrom = currentNode;
        openNode.pathLengthFromStart = neighbor.pathLengthFromStart;
      }
    });
  }
  return null;
};
